import { differenceInMinutes, format, intervalToDuration, parse, startOfDay } from 'date-fns';
import isNil from 'lodash/isNil';

type DateInput = string | Date | null | undefined;

const datePrettier = (dateString: DateInput): string => {
  if (isNil(dateString)) return '';
  const dt = new Date(dateString);
  const date = format(dt, 'dd.MM.yyyy');
  const time = format(dt, 'HH:mm');
  return `${date} ${time}`;
};

const datePrettierNoTime = (dateString: DateInput): string => {
  if (isNil(dateString)) return '';
  const dt = new Date(dateString);
  const date = format(dt, 'dd.MM.yyyy');
  return `${date}`;
};

const datePrettierNoTimeAndYear = (dateString: DateInput): string => {
  if (isNil(dateString)) return '';
  const dt = new Date(dateString);
  const date = format(dt, 'dd.MM');
  return `${date}`;
};

const dateRangePrettier = (startDate: DateInput, endDate: DateInput) => {
  if (isNil(startDate) || isNil(endDate)) return '';
  if (startDate === endDate) return datePrettier(startDate);
  return `${datePrettier(startDate)} - ${datePrettier(endDate)}`;
};

const dateRangePrettierNoTime = (startDate: DateInput, endDate: DateInput) => {
  if (isNil(startDate) || isNil(endDate)) return '';
  if (startDate === endDate) return datePrettierNoTime(startDate);
  return `${datePrettierNoTime(startDate)} - ${datePrettierNoTime(endDate)}`;
};

const dateRangePrettierNoTimeAndYear = (startDate: DateInput, endDate: DateInput) => {
  if (isNil(startDate) || isNil(endDate)) return '';
  if (startDate === endDate) return datePrettierNoTimeAndYear(startDate);
  return `${datePrettierNoTimeAndYear(startDate)} - ${datePrettierNoTimeAndYear(endDate)}`;
};

const prettierTime = (duration: number) => {
  if (Number.isNaN(duration)) return null;
  const { hours, minutes } = intervalToDuration({
    start: 0,
    end: duration * 60 * 1000,
  });
  const dt = startOfDay(new Date());
  const pd = parse(`${hours}:${minutes}`, 'HH:mm', dt);
  const timeFormat = `${hours ? "H'h'" : ''} ${minutes ? "mm'm'" : ''}`;
  return format(pd, timeFormat);
};

const durationToTime = (duration: number) => {
  if (Number.isNaN(duration)) return '';
  const { hours, minutes } = intervalToDuration({
    start: 0,
    end: duration * 60 * 1000,
  });
  const dt = startOfDay(new Date());
  const pd = parse(`${hours}:${minutes}`, 'HH:mm', dt);
  return format(pd, 'HH:mm');
};

const timeToDuration = (time: string) => {
  const dt = startOfDay(new Date());
  const pd = parse(time, 'HH:mm', dt);
  return differenceInMinutes(pd, dt);
};

const timeRangePrettier = (startDate: DateInput, endDate: DateInput) => {
  if (isNil(startDate) || isNil(endDate)) return '';
  const start = new Date(startDate);
  const end = new Date(endDate);
  return `${format(start, 'HH:mm')}-${format(end, 'HH:mm')}`;
};

const isValidTime = (time: string) => {
  const dt = startOfDay(new Date());
  return parse(time, 'HH:mm', dt).toString() !== 'Invalid Date';
};

const isValidTimeRange = (time: string) => {
  const [start, end] = time.split(' - ');
  if (isNil(start) || isNil(end)) return false;
  return isValidTime(start) && isValidTime(end);
};

const joinDate = (date: Date, time: Date): string => {
  const stringDate = format(date, 'yyyy-MM-dd');
  const stringTime = format(time, 'HH:mm:00');
  return `${stringDate}T${stringTime}`;
};

type WithActivityDates = {
  dateFrom?: Date | null;
  timeFrom?: Date | null;
  dateTo?: Date | null;
  timeTo?: Date | null;
  activeFrom?: string | null;
  activeTo?: string | null;
};

const resolveActiveDate = (date?: Date | null, time?: Date | null, endOfDay = false): string => {
  if (!date) return ' ';
  if (time) return joinDate(date, time);
  return `${format(date, 'yyyy-MM-dd')}T${endOfDay ? '23:59:59' : '00:00:00'}`;
};

const transformActivityDates = <T extends WithActivityDates>(
  object: T,
): Omit<T, 'dateFrom' | 'timeFrom' | 'dateTo' | 'timeTo'> & { activeFrom: string; activeTo: string } => {
  const { dateFrom, timeFrom, dateTo, timeTo, ...rest } = object;

  return {
    ...rest,
    activeFrom: resolveActiveDate(dateFrom, timeFrom),
    activeTo: resolveActiveDate(dateTo, timeTo, true),
  };
};

type TransformedDates = {
  dateFrom?: Date;
  timeFrom?: Date;
  dateTo?: Date;
  timeTo?: Date;
};

const transformStringDatesToDateObject = (object: { activeFrom?: string | null; activeTo?: string | null }) => {
  const newObject: TransformedDates = {};
  const { activeFrom, activeTo } = object;

  if (activeFrom) {
    newObject.dateFrom = new Date(activeFrom);
    newObject.timeFrom = new Date(activeFrom);
  }

  if (activeTo) {
    newObject.dateTo = new Date(activeTo);
    newObject.timeTo = new Date(activeTo);
  }

  return newObject;
};

export {
  datePrettier,
  datePrettierNoTime,
  dateRangePrettier,
  dateRangePrettierNoTime,
  dateRangePrettierNoTimeAndYear,
  prettierTime,
  durationToTime,
  timeToDuration,
  timeRangePrettier,
  isValidTime,
  isValidTimeRange,
  transformActivityDates,
  transformStringDatesToDateObject,
};
